import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useLenis } from "@/hooks/useLenis";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Chapters } from "@/components/Chapters";
import { Gallery } from "@/components/Gallery";
import { Amenities } from "@/components/Amenities";
import { Availability } from "@/components/Availability";
import { Reviews } from "@/components/Reviews";
import { BookingSection } from "@/components/BookingSection";
import { Footer } from "@/components/Footer";
import { Contacts } from "@/components/Contacts";
import { StickyBookingBar } from "@/components/StickyBookingBar";
import { useQuote } from "@/hooks/useQuote";
import { useAvailability } from "@/hooks/useAvailability";

const DEMO_PHOTOS = [
  { id: "demo-1", url: "https://images.unsplash.com/photo-1600494448850-6013c64ba722?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBhcGFydG1lbnQlMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc4ODY0NjU2Nnww&ixlib=rb-4.1.0&q=85", original_filename: "Sala" },
  { id: "demo-2", url: "https://images.unsplash.com/photo-1612152605347-f93296cb657d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxjb3p5JTIwYmVkcm9vbSUyMGFwYXJ0bWVudHxlbnwwfHx8fDE3ODg2NDY1NjZ8MA&ixlib=rb-4.1.0&q=85", original_filename: "Quarto" },
  { id: "demo-3", url: "https://images.unsplash.com/photo-1629079447777-1e605162dc8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBiYXRocm9vbXxlbnwwfHx8fDE3ODg2NDY1NjZ8MA&ixlib=rb-4.1.0&q=85", original_filename: "Casa de banho" },
  { id: "demo-4", url: "https://images.unsplash.com/photo-1560448205-d82bf18b9bcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxhcGFydG1lbnQlMjBiYWxjb255JTIwc3Vubnl8ZW58MHx8fHwxNzg4NjQ2NTY2fDA&ixlib=rb-4.1.0&q=85", original_filename: "Varanda" },
  { id: "demo-5", url: "https://images.unsplash.com/photo-1715985160053-d339e8b6eb94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBraXRjaGVuJTIwYXBhcnRtZW50fGVufDB8fHx8MTc4ODY0NjU2Nnww&ixlib=rb-4.1.0&q=85", original_filename: "Cozinha" },
];

const LISBOA_IMAGE =
  "https://images.unsplash.com/photo-1644871383003-2c1f3e499b00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxMaXNib24lMjBjaXR5JTIwdmlldyUyMHJvb2Z0b3BzJTIwYWZ0ZXJub29ufGVufDB8fHx8MTc5MTcyMzk5MXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  useLenis();
  const [property, setProperty] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [booking, setBooking] = useState({ checkIn: "", checkOut: "", guests: 2 });
  const [reviews, setReviews] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const quoteState = useQuote(property?.id, booking, refreshKey);
  const { booked, bookedMatchers } = useAvailability(property?.id, refreshKey);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: props } = await api.get("/properties");
        const prop = props[0];
        setProperty(prop);
        if (prop) {
          const [{ data: ph }, { data: rev }] = await Promise.all([
            api.get(`/properties/${prop.id}/photos`),
            api.get("/reviews"),
          ]);
          setPhotos(ph.map((p) => ({ ...p, url: fileUrl(p.storage_path) })));
          setReviews(rev);
        }
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, []);

  const galleryPhotos = photos.length ? photos : DEMO_PHOTOS;
  const pickPhoto = (keyword) =>
    galleryPhotos.find((p) => (p.original_filename || "").toLowerCase().includes(keyword))?.url;
  const chapterImages = {
    espaco: pickPhoto("sala"),
    experiencia: pickPhoto("terraco") || pickPhoto("marquise"),
    localizacao: LISBOA_IMAGE,
  };

  return (
    <div data-testid="home-page" className="bg-cream">
      <Navbar />
      <Hero property={property} booking={booking} setBooking={setBooking} quoteState={quoteState} booked={booked} bookedMatchers={bookedMatchers} />
      <Chapters property={property} images={galleryPhotos.map((p) => p.url)} chapterImages={chapterImages} />
      <Gallery photos={galleryPhotos} />
      <Amenities property={property} />
      <Availability property={property} bookedMatchers={bookedMatchers} />
      <Reviews reviews={reviews} />
      <BookingSection property={property || {}} booking={booking} setBooking={setBooking} quoteState={quoteState} onBooked={() => setRefreshKey((k) => k + 1)} booked={booked} bookedMatchers={bookedMatchers} />
      <Contacts property={property} />
      <Footer property={property} />
      <div className="h-20 md:hidden" aria-hidden="true" />
      <StickyBookingBar property={property} booking={booking} quote={quoteState.quote} />
    </div>
  );
}
